import { useEffect, useState } from "react";
import { COLORS } from "../../assets/utils/enums/colors";
import {
  NameIdType,
  TypeClientEnum,
  typeClientObj,
} from "../../assets/utils/enums/project";
import AppLayout, { MainBody } from "../../components/layout/app/layout";
import {
  CaptionText,
  LittleSubHeadline,
  TextBody,
} from "../../components/typography";
import { Button } from "../../components/ui/button";
import Input from "../../components/ui/inputs";
import { GeneralItemBlock } from "../project/add";
import {
  API_URL_COMPANY,
  API_URL_CONTACT,
  API_URL_PROJECT,
} from "../../constants";
import getData from "../../api/project-request";
import { useNavigate } from "react-router-dom";
import { useFormValidation } from "../../assets/utils/hooks/useFormsValidation";
import Headline from "../../components/ui/headline";
import { HeadlineTypeEnum } from "../../assets/utils/enums/headline";
import { ThemeTypeEnum } from "../../assets/utils/validation/types";
import { BodyDiv } from "../../components/components-style";
import CustomToast from "../../components/ui/custom-toast";
import toast from "react-hot-toast";
import { ToastTypeEnum } from "../../assets/utils/enums/toast";
import { authFetch } from "../../api/authFetch";
import { ValidationFormsModeEnum } from "../../assets/utils/validation/form/types";
import { validateClient } from "../../assets/utils/validation/form/validator-client";

export default function AddClientPage() {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [responsibleContact, setResponsibleContact] = useState<string>("");
  const [type, setType] = useState<string>(TypeClientEnum.POTENTIAL_CLIENT);

  const [types, setTypes] = useState<NameIdType[]>([]);

  const [filterResponsible, setFilterResponsible] = useState<
    { fullName: string; email: string }[]
  >([]);
  const [filterCompanies, setFilterCompanies] = useState<
    { id: number; name: string; responsibleEmail: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { errors, validate } = useFormValidation(validateClient);
  const navigate = useNavigate();

  useEffect(() => {
    const getTypes = () => {
      setIsLoading(true);
      setTimeout(async () => {
        const data = await getData(
          `https://back.galera.fm/api/CustomerTypes/get-all`
        );
        setTypes(data);
        setIsLoading(false);
      }, 50);
    };
    getTypes();
  }, []);

  useEffect(() => {
    const getResponsibleUsers = async () => {
      const data = await getData(
        `${API_URL_PROJECT}/get-users/search?query=${responsibleContact}`
      );
      setFilterResponsible(data.slice(0, 4));
    };

    const getCompanies = async () => {
      const data = await getData(`${API_URL_COMPANY}/search?query=${company}`);
      setFilterCompanies(data.slice(0, 4));
    };

    getResponsibleUsers();
    getCompanies();
  }, [responsibleContact, company]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const { hasErrors } = validate(
        {
          firstName,
          lastName,
          email,
          responsibleContact,
          type,
          phone,
          company,
        },
        ValidationFormsModeEnum.ADD
      );

      if (hasErrors) return;

      const response = await authFetch(`${API_URL_CONTACT}/add-new-client`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          companyName: company,
          responsibleUserEmail: responsibleContact,
          customerType: type,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Перевірте коректність введення даних!"}
          />
        ));
        return;
      }

      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Дані введені коректно, клієнт доданий!"}
        />
      ));
      setTimeout(() => {
        navigate("/clients/main");
      }, 300);
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
    }
  };

  return (
    <AppLayout
      className="d-flex flex-column align-items-center"
      blockType={"item-form"}
      loaderState={isLoading}
    >
      <Headline
        headline={HeadlineTypeEnum.CLIENTS}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Клієнтів"
        btn_link="/clients/main"
      />
      <MainBody className="align-items-stretch">
        <BodyDiv
          data-class="add-block"
          className="d-flex flex-column align-items-center"
        >
          <CaptionText>Основні дані клієнта</CaptionText>
          <div className="w-100">
            <span className="w-100 d-flex gap-sm-3 flex-sm-row flex-column">
              <Input
                attribute="firstName"
                value={firstName}
                setValue={setFirstName}
                errorText={errors.firstName}
                label="Ім’я"
                placeholder="Введіть ім’я"
              />
              <Input
                attribute="lastName"
                value={lastName}
                setValue={setLastName}
                errorText={errors.lastName}
                label="Прізвище"
                placeholder="Введіть прізвище"
              />
            </span>
            <Input
              attribute="company"
              value={company}
              setValue={setCompany}
              errorText={errors.company}
              companyArr={filterCompanies}
              label="Компанія"
              placeholder="Введіть назву"
            />
            <Input
              attribute="email"
              value={email}
              setValue={setEmail}
              errorText={errors.email}
              label="Електронна пошта"
              placeholder="Example@gmail.com"
            />
            <Input
              attribute="phone"
              value={phone}
              setValue={setPhone}
              errorText={errors.phone}
              label="Номер телефону"
              placeholder="Введіть номер"
            />
          </div>
        </BodyDiv>
        <BodyDiv
          data-class="add-block-other"
        >
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Відповідальний контакт</CaptionText>
            <Input
              attribute="contact"
              value={responsibleContact}
              setValue={setResponsibleContact}
              errorText={errors.responsibleContact}
              label=""
              contactsArr={filterResponsible}
              placeholder="Введіть пошту / ім’я"
            />
          </span>
          <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>Тип</CaptionText>
            <GeneralItemBlock>
              {types!.map((item: NameIdType) => {
                const isActive: boolean = type === item.name;
                return (
                  <Button
                    key={item.id}
                    bgcolor={
                      isActive
                        ? typeClientObj[item.name as TypeClientEnum].bgColor
                        : "transparent"
                    }
                    txtcolor={
                      isActive
                        ? typeClientObj[item.name as TypeClientEnum].txColor
                        : COLORS["bg-text-color"]
                    }
                    brcolor={
                      isActive
                        ? typeClientObj[item.name as TypeClientEnum].bgColor
                        : COLORS["bg-text-color"]
                    }
                    width="100%"
                    type="large"
                    brradius="24px"
                    className=""
                    onClick={() => {
                      setType(item.name);
                    }}
                  >
                    <TextBody>{item.name as TypeClientEnum}</TextBody>
                    {isActive && (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 32 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M28.708 8.70799L14.708 22.708C14.6151 22.8011 14.5048 22.875 14.3833 22.9254C14.2618 22.9758 14.1315 23.0018 14 23.0018C13.8685 23.0018 13.7382 22.9758 13.6167 22.9254C13.4952 22.875 13.3849 22.8011 13.292 22.708L5.29199 14.708L6.70799 13.292L14 20.586L27.294 7.29199L28.708 8.70799Z"
                          fill={
                            isActive
                              ? typeClientObj[item.name as TypeClientEnum]
                                  .txColor
                              : "transparent"
                          }
                        />
                      </svg>
                    )}
                  </Button>
                );
              })}
            </GeneralItemBlock>
          </span>
        </BodyDiv>
      </MainBody>
      <Button
        bgcolor={COLORS.secondary1}
        txtcolor={COLORS.white}
        brcolor={"transparent"}
        width="30%"
        className="center-btn"
        brradius="24px"
        type="large"
        onClick={handleSave}
      >
        <LittleSubHeadline>Зберегти зміни</LittleSubHeadline>
      </Button>
    </AppLayout>
  );
}
